import React, { useState } from 'react';
import { Box, Plus, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { GitHubConfigModal } from './integrations/GitHubConfigModal';
import { SlackConfigModal } from './integrations/SlackConfigModal';
import { JiraConfigModal } from './integrations/JiraConfigModal';
import { SplunkConfigModal } from './integrations/SplunkConfigModal';

// Define an interface for the integration object
interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
}

const integrations: Integration[] = [
  {
    id: '1',
    name: 'GitHub',
    description: 'Connect your GitHub repositories for security scanning',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
    connected: true
  },
  {
    id: '2',
    name: 'Slack',
    description: 'Get security alerts in your Slack channels',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/slack/slack-original.svg',
    connected: false
  },
  {
    id: '3',
    name: 'Jira',
    description: 'Create issues for security findings automatically',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg',
    connected: true
  },
  {
    id: '4',
    name: 'Splunk',
    description: 'Forward security events to your Splunk instance',
    icon: 'https://www.splunk.com/content/dam/splunk2/images/Planet-Splunk.png',
    connected: false
  },

  {
    id: '5',
    name: 'PagerDuty',
    description: 'Create incidents and alerts for critical security events',
    icon: 'https://play-lh.googleusercontent.com/E-zhAf4KJ6JDDXmQfQxBprn2sATGYUMkOEqLQX5HAQQtiwDZJg4c8sQd7deb6nCZCwU',
    connected: false
  },
  {
    id: '6',
    name: 'ServiceNow',
    description: 'Create and track security incidents in ServiceNow',
    icon: 'https://companieslogo.com/img/orig/NOW-6d5a01eb.png?t=1720244493',
    connected: false
  },
  {
    id: '7',
    name: 'Elastic SIEM',
    description: 'Forward security events to your Elastic SIEM instance',
    icon: 'https://camo.githubusercontent.com/6625f49cb63c295bf329b427d22b753ca70fef79858d43fd3cda0241a5f3e0ba/68747470733a2f2f7777772e656c61737469632e636f2f7374617469632d7265732f696d616765732f656c61737469632d6c6f676f2d3230302e706e67',
    connected: false
  },
  {
    id: '8',
    name: 'IBM QRadar',
    description: 'Forward security events to your IBM QRadar SIEM',
    icon: 'https://www.metabyte.com/files/picturemodule/58af745e55869_3.png',
    connected: false
  },
];

export const IntegrationSettings = () => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'add' | 'configure' | 'disconnect';
    integration: Integration | null;
  }>({
    isOpen: false,
    type: 'add' as const,
    integration: null
  });

  const renderModal = () => {
    if (!modalState.integration && modalState.type !== 'add') return null;

    // Base props common to all modals
    const baseProps = {
      isOpen: modalState.isOpen,
      onClose: () => setModalState({ ...modalState, isOpen: false }),
    };

    // For modals that expect integration to be undefined or an object (not null)
    // and type to be only 'add' or 'configure' (not 'disconnect')
    const getModalProps = () => {
      const { type } = modalState;
      // Convert 'disconnect' to 'configure' for modal
      const modalType = type === 'disconnect' ? 'configure' : type;
      
      // Handle the integration property - modals expect integration to be undefined or an object, not null
      const modalIntegration = modalState.integration || undefined;
      
      return {
        ...baseProps,
        type: modalType as 'add' | 'configure',
        integration: modalIntegration
      };
    };

    switch (modalState.integration?.name) {
      case 'GitHub':
        return <GitHubConfigModal {...getModalProps()} />;
      case 'Slack':
        return <SlackConfigModal {...getModalProps()} />;
      case 'Jira':
        return <JiraConfigModal {...getModalProps()} />;
      case 'Splunk':
        return <SplunkConfigModal {...getModalProps()} />;
      default:
        if (modalState.type === 'add') {
          return <GitHubConfigModal {...getModalProps()} />;
        }
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Box className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Integrations</h2>
          </div>
          <Button onClick={() => setModalState({ isOpen: true, type: 'add', integration: null })} disabled>
            <Plus className="h-4 w-4 mr-2" />
            Add Integration
          </Button>
        </div>

        <div className="space-y-6">
          {/* Integrations List */}
          <div className="space-y-4">
            {integrations.map((integration) => (
              <div key={integration.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group">
                <div className="flex items-center space-x-4">
                  <div className="p-2 rounded-lg bg-gray-800/50">
                    <img
                      src={integration.icon}
                      alt={integration.name}
                      className="h-8 w-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-white">{integration.name}</div>
                    <div className="text-sm text-gray-400">{integration.description}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {integration.connected ? (
                    <>
                      <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        <span className="text-xs font-medium">Connected</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModalState({
                          isOpen: true,
                          type: 'configure',
                          integration
                        })}
                        disabled
                      >
                        Configure
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-400"
                        onClick={() => setModalState({
                          isOpen: true,
                          type: 'disconnect',
                          integration
                        })}
                        disabled
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setModalState({
                        isOpen: true,
                        type: 'add',
                        integration
                      })}
                      disabled
                    >
                      Connect
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Available Integrations */}
          {/* <div className="pt-6 border-t border-gray-800">
            <h3 className="text-sm font-medium text-white mb-4">Contact support for the following integrations:</h3>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-gray-800/50 mx-auto mb-3">
                    <Box className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-white">Integration {i}</div>
                    <div className="text-xs text-gray-400 mt-1">Coming soon</div>
                  </div>
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </div>

      {renderModal()}
    </div>
  );
};